import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { drainOfflineQueue } from "../lib/offlineSync";
import {
  OFFLINE_QUEUE_EVENT,
  listQueuedOfflineFinds,
} from "../lib/offlineQueue";
import { useOnlineStatus } from "./use-online-status";

// Phase F3 — single mounted coordinator that drains the offline find
// queue. Mounted once in <AppLayout>; multiple consumers reading
// `pendingCount` is fine (React Query / state subscription pattern).
//
// Drain triggers:
//   1. App mount, when navigator.onLine is true. Catches the case where
//      the player force-quit while offline and reopens once on wifi.
//   2. The `online` window event. Catches mid-session reconnects.
//   3. Manual `syncNow()` from the offline badge — gives the player an
//      escape hatch when the auto-drain hasn't fired yet.
//
// The drain itself is debounced via a ref-guarded inFlight bit so
// rapid-fire `online` events (some browsers fire it twice) don't kick
// off two parallel drains and double-mark attempts.
export function useOfflineSync(): {
  pendingCount: number;
  isSyncing: boolean;
  syncNow: () => Promise<void>;
  refreshPendingCount: () => Promise<void>;
} {
  const qc = useQueryClient();
  const { isOnline } = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const inFlight = useRef(false);

  const refreshPendingCount = useCallback(async () => {
    try {
      const queued = await listQueuedOfflineFinds();
      setPendingCount(queued.length);
    } catch {
      // IndexedDB unavailable (private mode, quota); badge stays at 0.
      setPendingCount(0);
    }
  }, []);

  const syncNow = useCallback(async () => {
    if (inFlight.current) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await refreshPendingCount();
      return;
    }
    inFlight.current = true;
    setIsSyncing(true);
    try {
      await drainOfflineQueue({ qc });
    } finally {
      inFlight.current = false;
      setIsSyncing(false);
      await refreshPendingCount();
    }
  }, [qc, refreshPendingCount]);

  // Initial pending count on mount + opportunistic drain if online.
  useEffect(() => {
    void refreshPendingCount();
    if (typeof navigator !== "undefined" && navigator.onLine) {
      void syncNow();
    }
    // We intentionally only run this once at mount. Subsequent drains
    // are triggered by the online-event listener below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-drain on reconnect.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => void syncNow();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [syncNow]);

  // Refresh the badge count whenever the queue's contents change. This
  // is what makes "I just attached a photo while offline" show the
  // header sync-now chip immediately — without it, pendingCount stays
  // at its stale value until the next isOnline flip.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => void refreshPendingCount();
    window.addEventListener(OFFLINE_QUEUE_EVENT, onChange);
    return () => window.removeEventListener(OFFLINE_QUEUE_EVENT, onChange);
  }, [refreshPendingCount]);

  // Refresh badge count whenever the network state flips, so the badge
  // text updates immediately (online → "0 queued" → hides).
  useEffect(() => {
    void refreshPendingCount();
  }, [isOnline, refreshPendingCount]);

  return { pendingCount, isSyncing, syncNow, refreshPendingCount };
}
